import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function ManchesterCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Manchester Pothole Claims | GREEN Rated, AMBER Condition | Fixtyer"
        description="Manchester City Council earns GREEN overall from the DfT but AMBER on road condition. 1,020km of unclassified roads, 15% in RED, and 76,985 potholes filled in five years — what it means for your claim."
        canonical="/councils/manchester"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Manchester', url: '/councils/manchester' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Does Manchester\'s GREEN overall rating block my pothole claim?',
            answer: 'No — but it changes your approach. GREEN overall reflects strong spend and best-practice scorecards, not perfect roads. The DfT rates Manchester AMBER on condition. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on the headline rating.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 1,020km — 76% of Manchester\'s 1,343km carriageway network. At the latest survey, 15% of U-roads were in RED condition and only 67% in GREEN. Most pothole claims occur on these local streets, not the A-roads where Manchester performs best.',
          },
          {
            question: 'Manchester surveys half its network each year — what does that mean for Section 58?',
            answer: 'The council assesses approximately 50% of its network annually, meaning any given road may go up to two years between condition surveys. For a defect on an unsurveyed stretch, ask when your road was last measured and whether routine safety inspections under the GM Framework would have caught it.',
          },
          {
            question: 'Pothole repairs fell to 10,120 in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Manchester still filled 10,120 potholes in 2024/25 and expects about 10,000 more in 2025/26. The council itself notes that some defects will still be found despite recent improvements, and that the figure may change with severe winter weather.',
          },
          {
            question: 'Manchester spends well over triple its DfT allocation — can I still claim?',
            answer: 'Yes. Capital spend reached £21.8m in 2024/25 against a DfT allocation of £8.0m, with 86% classed as preventative. That strengthens Manchester\'s Section 58 defence on paper — but AMBER condition and 15% RED U-roads show defects still form. Your claim lives on the specific pothole, prior reports, and inspection records.',
          },
          {
            question: 'Why is Manchester AMBER on condition when the council says roads improved?',
            answer: 'Both can be true. Manchester cut roads in poor or very poor condition from 25% in 2017 to 12% in 2024 — but footways have not improved, U-roads still have 15% in RED, and the council admits its published condition figures differ slightly from the official DfT statistics due to methodology changes.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Manchester City Council"
        councilSlug="manchester"
        rating="green"
      />
      <SpeakableSchema
        url="/councils/manchester"
        headline="Manchester Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Manchester</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="green" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Manchester: GREEN Overall, AMBER Roads Underneath
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Manchester City Council earns <strong className="text-emerald-400">GREEN</strong> overall from the DfT — 
            driven by strong spend and best-practice scorecards. But road <strong className="text-amber-400">condition 
            is AMBER</strong>, and 1,020km of unclassified streets — 76% of the network — still carry 
            <strong className="text-rose-400"> 15% in RED condition</strong>. The rating rewards how the council 
            manages its £3bn highway asset; your claim turns on the specific defect.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">1,020km</div>
            <div className="text-white font-medium mt-2">Unclassified roads — 76% of the network</div>
            <div className="text-brand-300 text-sm mt-1">
              At the latest survey, 15% of U-roads were in RED condition and only 67% in GREEN. 
              This is where most Manchester pothole claims originate.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* DfT Scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Split Verdict
          </h2>
          <p className="text-surface-600 mb-8">
            GREEN overall despite AMBER condition — the DfT rewards process and investment, not perfect tarmac
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Manchester City Council DfT Road Maintenance Ratings 2025-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Metric</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall Rating</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Road Condition</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Best Practice</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">What GREEN Overall Means</h3>
              <p className="text-emerald-800 text-sm">
                Manchester performs above average on how it spends, plans, and manages its highway assets. 
                Since 2017, internal borrowing has funded over £125m of capital investment. The council 
                classifies 85–86% of recent spend as preventative — a well-run system by national standards.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">What AMBER Condition Means For You</h3>
              <p className="text-amber-800 text-sm">
                Condition is the scorecard that maps to the road beneath your tyres. Manchester's own data 
                shows 15% of unclassified roads in RED and footways stuck at 14% in poor or very poor 
                condition. GREEN overall does not mean the pothole that damaged your car was reasonably 
                maintained — only that the council's systems look competent on paper.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Network Scale */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            1,343km of Roads, £3bn of Asset
          </h2>
          <p className="text-surface-600 mb-8">
            Manchester's highway network from Appendix 1 of the council's transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Length</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Share of network</th>
                </tr>
              </thead>
              <tbody className="text-sm divide-y divide-surface-100">
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center">167 km</td>
                  <td className="px-6 py-4 text-center">12.4%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center">156 km</td>
                  <td className="px-6 py-4 text-center">11.6%</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-bold text-amber-900">Unclassified roads</td>
                  <td className="px-6 py-4 text-center font-bold">1,020 km</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-700">75.9%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total carriageways</td>
                  <td className="px-6 py-4 text-center font-bold">1,343 km</td>
                  <td className="px-6 py-4 text-center">100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-600 text-sm mb-8">
            The network also includes 2,668 km of footways, 260 km of cycleways, 350+ bridges and structures, 
            and over 120,000 drainage gullies — a highway asset valued at over £3 billion.
          </p>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Claim-Relevant Split</h3>
            <p className="text-brand-200 mb-4">
              Manchester's investment success story is told largely through A-roads — 78% in GREEN condition 
              in 2024, RED down from 8% to 6% since 2020. But three-quarters of the network is unclassified 
              roads, where RED condition still sits at 15% and only 67% are GREEN.
            </p>
            <p className="text-white font-medium">
              A GREEN overall rating built on strategic-road performance does not automatically protect 
              the council on a residential street in Moss Side, Wythenshawe, or Harpurhey.
            </p>
          </div>
        </div>
      </section>

      {/* Condition Data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five Years of Condition Data
          </h2>
          <p className="text-surface-600 mb-8">
            RED, amber and green bandings by road class — Appendix 3 of Manchester's transparency report
          </p>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (167 km — improving)</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">8%</td>
                    <td className="px-4 py-2 text-center">19%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">8%</td>
                    <td className="px-4 py-2 text-center">16%</td>
                    <td className="px-4 py-2 text-center">76%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">6%</td>
                    <td className="px-4 py-2 text-center font-bold">16%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">78%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Credit where due: Manchester's A-roads have genuinely improved. But they are barely one-eighth of the network.
            </p>
          </div>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (156 km — stable)</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">10%</td>
                    <td className="px-4 py-2 text-center">18%</td>
                    <td className="px-4 py-2 text-center">72%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">9%</td>
                    <td className="px-4 py-2 text-center">17%</td>
                    <td className="px-4 py-2 text-center">74%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">6%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">19%</td>
                    <td className="px-4 py-2 text-center font-bold">75%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B/C roads look broadly stable — but amber banding ticked up to 19% in 2024, meaning nearly 
              a quarter of these roads may need maintenance soon.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (1,020 km — the claim hotspot)</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">21%</td>
                    <td className="px-4 py-2 text-center">18%</td>
                    <td className="px-4 py-2 text-center">61%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">16%</td>
                    <td className="px-4 py-2 text-center">18%</td>
                    <td className="px-4 py-2 text-center">66%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">15%</td>
                    <td className="px-4 py-2 text-center font-bold">18%</td>
                    <td className="px-4 py-2 text-center font-bold">67%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              U-road RED condition improved from 21% to 14% between 2020 and 2023 — then rose again to 15% in 2024. 
              That is roughly 153km of residential and neighbourhood streets in RED condition. A third of the 
              U-road network (15% red + 18% amber) needs or will soon need maintenance.
            </p>
          </div>

          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
            <h3 className="font-semibold text-rose-900 mb-4">Footways: stuck at 14% poor or very poor</h3>
            <p className="text-rose-800 text-sm mb-4">
              Carriageways in grades 4 and 5 fell from 25% in 2017 to 12% in 2024. Footways moved from 16% 
              to 14% over the same period — essentially flat while roads received the investment focus.
            </p>
            <QuoteCard
              quote="Most investment focused on roads. This is why footway conditions have generally not improved. Now, footways are a priority in upcoming maintenance plans, along with local and neighbourhood roads."
              source="Manchester City Council Local Highways Maintenance Transparency Report 2025/26"
            />
          </div>
        </div>
      </section>

      {/* Survey Methodology & Section 58 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Manchester finds defects — and what the council admits it does not repair
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Condition Surveys</h3>
              <QuoteCard
                quote="The condition of our entire highways network is currently assessed every two years (approximately half of the network is surveyed each year). This is done by our survey contractor using video assessments and, if needed, on-foot data collection."
                source="Manchester City Council Local Highways Maintenance Transparency Report 2025/26"
              />
              <p className="text-surface-600 text-sm mt-4">
                Appendix 3 confirms annual surveys capture about 50% of the total network each year — 
                all road classes surveyed within a two-year window. Ask when your specific road was last 
                condition-surveyed.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Safety Inspections</h3>
              <p className="text-surface-600 text-sm mb-3">
                Manchester operates under the GM Framework for Highway Safety Inspections, endorsed by 
                all 10 Greater Manchester highway authorities, alongside its own Safety Inspection Policy. 
                A team of highways inspectors assesses adopted roads and footways, ordering repairs where needed.
              </p>
              <QuoteCard
                quote="We operate a risk-based approach to fixing issues that are found through inspections or reported to us, meaning that serious safety problems are always prioritised for repair."
                source="Manchester City Council Local Highways Maintenance Transparency Report 2025/26"
              />
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-3">Defects the Council Chooses Not to Fix</h3>
            <QuoteCard
              quote="Some minor defects don't pose a danger to people or damage the road, so they aren't marked for repair."
              source="Manchester City Council Local Highways Maintenance Transparency Report 2025/26"
            />
            <p className="text-amber-800 text-sm mt-4">
              Manchester defines a pothole as a round dip or hole 40mm deep or more. A defect below that 
              threshold may exist on the network without being marked for repair — even if it damaged your 
              vehicle. Your evidence must show the defect met the council's own intervention criteria.
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 58 Questions Worth Asking</h3>
            <p className="text-brand-200 mb-4">
              Under Section 58 of the Highways Act 1980, Manchester must show it took reasonable care 
              to maintain the specific highway. For a well-rated council, your claim probes the gap between 
              policy and the defect:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road in the 50% surveyed this year, or the half that was not?</li>
              <li>• Did the defect meet the 40mm pothole threshold and GM inspection intervention criteria?</li>
              <li>• Was it reported before your incident — via FixMyStreet, the council website, or a prior inspection?</li>
              <li>• On a U-road with 15% RED network-wide, what was done about your street specifically?</li>
            </ul>
            <p className="text-white font-medium">
              A GREEN rating proves competent systems exist. It does not prove those systems caught 
              your pothole in time.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole Repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            76,985 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes from Appendix 2 — the network still produces defects at scale
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">16,582</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">13,083</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">22,000</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">15,200</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">10,120</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">76,985</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The 2022/23 Spike</h3>
              <p className="text-surface-600 text-sm">
                Manchester filled 22,000 potholes in 2022/23 — nearly double the 2024/25 figure. 
                That year-over-year volatility tells you defects form in bursts, often driven by weather, 
                not steady improvement. A lower count in 2024/25 does not mean your road was inspected 
                more recently.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">2025/26 Forecast</h3>
              <QuoteCard
                quote="We expect to fill about 10,000 potholes which will be identified in 2025/26. Our highway safety inspectors will find most during their regular inspections, along with those that customers report. This number may change due to environmental factors, like severe winter weather."
                source="Manchester City Council Local Highways Maintenance Transparency Report 2025/26"
              />
            </div>
          </div>

        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            Manchester's GREEN spend scorecard is earned — capital investment far exceeds the DfT allocation. 
            But revenue reactive work still runs to millions each year.
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 p-6 mb-8 overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Manchester highway maintenance spending 2020-2026</caption>
              <thead>
                <tr className="border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">£8.44m</td>
                  <td className="px-4 py-3 text-center">£20.86m</td>
                  <td className="px-4 py-3 text-center">£3.42m</td>
                  <td className="px-4 py-3 text-center text-emerald-400">86%</td>
                  <td className="px-4 py-3 text-center">14%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">£8.04m</td>
                  <td className="px-4 py-3 text-center">£21.83m</td>
                  <td className="px-4 py-3 text-center">£3.69m</td>
                  <td className="px-4 py-3 text-center text-emerald-400">86%</td>
                  <td className="px-4 py-3 text-center">14%</td>
                </tr>
                <tr className="bg-brand-800/50">
                  <td className="px-4 py-3 font-medium">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center">£6.44m</td>
                  <td className="px-4 py-3 text-center">£21.09m</td>
                  <td className="px-4 py-3 text-center">£3.60m</td>
                  <td className="px-4 py-3 text-center text-emerald-400">85%</td>
                  <td className="px-4 py-3 text-center">15%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div>
              <div className="text-3xl font-bold text-emerald-400">£21.8m</div>
              <div className="text-brand-300 text-sm">Capital spend 2024/25</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-400">2.7×</div>
              <div className="text-brand-300 text-sm">Capital spend vs DfT allocation</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-400">86%</div>
              <div className="text-brand-300 text-sm">Estimated preventative share 2024/25</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-white mb-3">2024/25 Capital Delivery</h3>
              <ul className="text-brand-200 text-sm space-y-2">
                <li>• Road resurfacing — 31 km</li>
                <li>• Road overlay treatments — 23 km</li>
                <li>• Footway treatments — 66 km</li>
                <li>• Bridges and structures — 8 schemes</li>
              </ul>
            </div>
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-white mb-3">2025/26 Planned Programme</h3>
              <ul className="text-brand-200 text-sm space-y-2">
                <li>• Road resurfacing — 22 km</li>
                <li>• Road overlay treatments — 33 km</li>
                <li>• Footway treatments — 63 km (149 schemes)</li>
                <li>• Bridges and structures — 6 schemes proposed</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Council Admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Manchester Acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Direct quotes from the council's 2025/26 transparency report
          </p>

          <div className="space-y-6 mb-8">
            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On the Improvement Narrative</h3>
              <QuoteCard
                quote="In 2017, 25% of all our roads were in this poor or very poor condition, but this has now reduced to about 12% in 2024."
                source="Manchester City Council Local Highways Maintenance Transparency Report 2025/26"
              />
              <p className="text-surface-600 mt-3 text-sm">
                A genuine improvement — but the council's own footway data flatlined at 14% poor or very poor, 
                and U-road RED ticked back up in 2024.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On Claims and Proactive Maintenance</h3>
              <QuoteCard
                quote="This pro-active work reduces costly reactive repairs and lowers the chance of third-party claims."
                source="Manchester City Council Local Highways Maintenance Transparency Report 2025/26"
              />
              <p className="text-surface-600 mt-3 text-sm">
                Manchester explicitly links preventative maintenance to claim reduction — yet still expects 
                10,000 pothole repairs in 2025/26. The proactive programme has not eliminated defects.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On Data Methodology</h3>
              <QuoteCard
                quote="The numbers in these tables are slightly different from those on the official Road Condition Statistics website. This is because: Some changes were made to C and U road lengths. The way we have split the 1-5 grading system into red, amber, and green condition has changed slightly and this has been applied to previous years for consistency."
                source="Manchester City Council Local Highways Maintenance Transparency Report 2025/26"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Claim Strategy */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Rated, AMBER-Condition Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Manchester is not Derbyshire — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend — capital investment 2–3× the DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN best practice — ISO-aligned asset management, lifecycle planning, GM Framework inspections
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition genuinely improving — 78% GREEN in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 85–86% of spend classed as preventative in recent years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Roads in poor/very poor condition halved since 2017 (25% → 12%)
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence on strategic and recently resurfaced routes. 
                Generic negligence arguments will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — DfT rates the roads mid-table despite GREEN overall
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 1,020km of U-roads with 15% RED and only 67% GREEN
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 50% of the network condition-surveyed per year — two-year blind spots
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 76,985 potholes filled in five years — defects still form at scale
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Footways stuck at 14% poor/very poor — council admits roads took priority
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Minor defects below repair threshold left on the network by design
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and best-practice scorecards, your claim lives or dies on 
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council online reports) — proof of actual notice</li>
              <li>• Photos showing depth, width and visible age — especially whether the defect exceeds 40mm</li>
              <li>• Road class — on a U-road, the 15% network-wide RED rate and two-year survey cycle are your structural arguments</li>
              <li>• Whether the defect met GM Framework intervention criteria and how long it remained unrepaired</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Manchester's own transparency data where it helps you — without pretending the council is failing.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Manchester?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-rated council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road condition argument</li>
                <li>• No two-year survey-gap analysis</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ AMBER condition vs GREEN overall framed honestly</li>
                <li>✅ 1,020km U-road network data cited</li>
                <li>✅ 76,985 potholes in five years documented</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Manchester</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=manchester"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Manchester Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Manchester's GREEN overall rating block my pothole claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No — but it changes your approach. GREEN overall reflects strong spend and best-practice scorecards, 
                not perfect roads. The DfT rates Manchester AMBER on condition. Section 58 turns on whether the 
                specific defect that damaged your vehicle was reasonably inspected and repaired, not on the headline rating.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 1,020km — 76% of Manchester's 1,343km carriageway network. At the latest 
                survey, 15% of U-roads were in RED condition and only 67% in GREEN. Most pothole claims occur on 
                these local streets, not the A-roads where Manchester performs best.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Manchester surveys half its network each year — what does that mean for Section 58?
              </h3>
              <p className="text-surface-600 text-sm">
                The council assesses approximately 50% of its network annually, meaning any given road may go up to 
                two years between condition surveys. For a defect on an unsurveyed stretch, ask when your road was 
                last measured and whether routine safety inspections under the GM Framework would have caught it.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs fell to 10,120 in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Manchester still filled 10,120 potholes in 2024/25 and expects about 10,000 more in 2025/26. 
                The council itself notes that some defects will still be found despite recent improvements, and 
                that the figure may change with severe winter weather.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Manchester spends well over triple its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Capital spend reached £21.8m in 2024/25 against a DfT allocation of £8.0m, with 86% classed as 
                preventative. That strengthens Manchester's Section 58 defence on paper — but AMBER condition and 
                15% RED U-roads show defects still form. Your claim lives on the specific pothole, prior reports, 
                and inspection records.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is Manchester AMBER on condition when the council says roads improved?
              </h3>
              <p className="text-surface-600 text-sm">
                Both can be true. Manchester cut roads in poor or very poor condition from 25% in 2017 to 12% in 2024 — 
                but footways have not improved, U-roads still have 15% in RED, and the council admits its published 
                condition figures differ slightly from the official DfT statistics due to methodology changes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.manchester.gov.uk/downloads/download/7790/highway_maintenance_report_202526"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Manchester City Council Local Highways Maintenance Transparency Report 2025/26
            </a>.
            Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
